// Pure window-manager state machine for the bottom-right messaging dock
// (issue #194, multi-window slice).
//
// The dock is a Messenger/LinkedIn-style desktop surface: several conversation
// windows open at once, tiled bottom-right, each independently minimizable to a
// circular "chat head". This reducer owns *which* windows exist and their
// focus/minimized state; the per-window message history, draft, scroll and
// uploads live in the mounted React components keyed by `conversationId`, never
// here — so recreating a `DockWindow` value for an already-open conversation is
// lossless.
//
// Model invariants (enforced here so the view layer never has to defend them,
// engineering-principles invariant #4):
//   - `windows` is in display order: the order windows arrived in the dock. A
//     window arrives when it is opened, or restored from a chat head. Focusing
//     a window never moves it, so a window never jumps under the cursor.
//   - Recency is separate from position: `lastActive` is the `activations`
//     count at the window's last activation. It alone decides which window
//     collapses to make room and which conversation the total cap closes.
//   - At most `MAX_OPEN_WINDOWS` are expanded at once; overflow windows stay
//     mounted but `minimized` (rendered as chat heads), so no draft/scroll/
//     history is ever lost to the expanded cap.
//   - At most `MAX_DOCK_WINDOWS` conversations are open at all. Past that the
//     least recently used one closes; its unsent draft survives in the
//     provider, so reopening it brings the text back.
//   - `focusedId` is null or names an expanded window. Focus is only ever
//     GIVEN by an explicit activation (open, focus, chat-head restore) — never
//     inherited when the focused window is minimized or closed. Focus gates
//     mark-read, so a fallback focus would clear the unread of a window the
//     viewer never interacted with; on mobile (one full-screen sheet for the
//     focused window) it would also swap a different conversation in under
//     the viewer's thumb.
//   - Activating a window collapses the `launcher` list (it is a launcher, not
//     a tile, so the window reclaims its width). Minimizing or closing a
//     window never moves the launcher.

/** Max simultaneously-expanded (tiled) windows on desktop. Beyond this, the
 *  least-recently-focused expanded window collapses to a chat head. Sized to
 *  what fits beside the sidebar at 360px on a ~1280px viewport. */
export const MAX_OPEN_WINDOWS = 3;

/** Max open conversations, expanded or not. Every open window stays mounted
 *  with its own conversation query, so this bounds the dock's background work
 *  as well as the chat-head list. */
export const MAX_DOCK_WINDOWS = 10;

export interface DockWindow {
  readonly conversationId: string;
  /** true = collapsed to a chat head; false = expanded/tiled. */
  readonly minimized: boolean;
  /** `DockState.activations` at this window's last activation. */
  readonly lastActive: number;
}

export type DockLauncher = 'collapsed' | 'list';

export interface DockState {
  readonly launcher: DockLauncher;
  readonly windows: readonly DockWindow[];
  readonly focusedId: string | null;
  /** Count of activations so far; stamps `lastActive`. */
  readonly activations: number;
}

export type DockAction =
  | { readonly type: 'toggleLauncher' }
  | { readonly type: 'openLauncher' }
  | { readonly type: 'openThread'; readonly conversationId: string }
  | { readonly type: 'focusWindow'; readonly conversationId: string }
  | { readonly type: 'minimizeWindow'; readonly conversationId: string }
  | { readonly type: 'closeWindow'; readonly conversationId: string };

export const initialDockState: DockState = {
  launcher: 'collapsed',
  windows: [],
  focusedId: null,
  activations: 0,
};

/** The least recently used window passing `eligible`, or undefined. */
function leastRecent(
  windows: readonly DockWindow[],
  eligible: (w: DockWindow) => boolean,
): DockWindow | undefined {
  let oldest: DockWindow | undefined;
  for (const w of windows) {
    if (eligible(w) && (oldest === undefined || w.lastActive < oldest.lastActive)) oldest = w;
  }
  return oldest;
}

/** Minimize the least recently used expanded windows until at most
 *  `MAX_OPEN_WINDOWS` remain expanded, then close the least recently used
 *  conversations until at most `MAX_DOCK_WINDOWS` remain. `keepId` (the window
 *  just activated) is never touched. */
function enforceCaps(windows: readonly DockWindow[], keepId: string): readonly DockWindow[] {
  let result = windows;
  while (result.filter((w) => !w.minimized).length > MAX_OPEN_WINDOWS) {
    const evict = leastRecent(result, (w) => !w.minimized && w.conversationId !== keepId);
    if (evict === undefined) break;
    result = result.map((w) => (w === evict ? { ...w, minimized: true } : w));
  }
  while (result.length > MAX_DOCK_WINDOWS) {
    const evict = leastRecent(result, (w) => w.conversationId !== keepId);
    if (evict === undefined) break;
    result = result.filter((w) => w !== evict);
  }
  return result;
}

/** Open-or-focus a conversation: expand it, stamp it most recent, give it
 *  focus, enforce the caps, and collapse the launcher list. An expanded window
 *  keeps its place; a new or restored one arrives at the end (nearest the
 *  corner). Used by `openThread` and `focusWindow`/chat-head restore. */
function activate(state: DockState, conversationId: string): DockState {
  const activations = state.activations + 1;
  const current = state.windows.find((w) => w.conversationId === conversationId);
  const activated: DockWindow = { conversationId, minimized: false, lastActive: activations };
  const windows =
    current !== undefined && !current.minimized
      ? state.windows.map((w) => (w === current ? activated : w))
      : [...state.windows.filter((w) => w !== current), activated];
  return {
    launcher: 'collapsed',
    windows: enforceCaps(windows, conversationId),
    focusedId: conversationId,
    activations,
  };
}

/** Focus after `conversationId` leaves the expanded set: kept if it pointed at
 *  another window, otherwise dropped (see the focus invariant above). */
function focusWithout(state: DockState, conversationId: string): string | null {
  return state.focusedId === conversationId ? null : state.focusedId;
}

export function dockReducer(state: DockState, action: DockAction): DockState {
  switch (action.type) {
    case 'toggleLauncher':
      return { ...state, launcher: state.launcher === 'collapsed' ? 'list' : 'collapsed' };

    case 'openLauncher':
      return state.launcher === 'list' ? state : { ...state, launcher: 'list' };

    case 'openThread':
      return activate(state, action.conversationId);

    case 'focusWindow':
      return state.windows.some((w) => w.conversationId === action.conversationId)
        ? activate(state, action.conversationId)
        : state;

    case 'minimizeWindow': {
      const target = state.windows.find((w) => w.conversationId === action.conversationId);
      if (!target || target.minimized) return state;
      const windows = state.windows.map((w) =>
        w.conversationId === action.conversationId ? { ...w, minimized: true } : w,
      );
      return { ...state, windows, focusedId: focusWithout(state, action.conversationId) };
    }

    case 'closeWindow': {
      if (!state.windows.some((w) => w.conversationId === action.conversationId)) return state;
      const windows = state.windows.filter((w) => w.conversationId !== action.conversationId);
      return { ...state, windows, focusedId: focusWithout(state, action.conversationId) };
    }
  }
}
